import { tierKey, tierLabel } from '../utils/tierStyles';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { UpgradePlanModal } from './UpgradePlanModal';
import {
  Crown, Calendar, Wallet as WalletIcon, CheckCircle2, TrendingUp, Loader2,
} from 'lucide-react';

const fmtPHP = (n) => `PHP ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-PH', { dateStyle: 'medium' }) : '—');

/**
 * Member-facing "My Plan" page (replaces the bad UX of routing to the public
 * /pricing page when a member is already subscribed). Shows the current
 * subscription's plan, price, next billing date, and a one-click Upgrade
 * button (reuses the existing UpgradePlanModal). For inactive members, falls
 * back to a clear "Pick a plan" CTA that lands on /pricing.
 */
export function MyPlanPage() {
  const { user, api } = useAuth();
  const [sub, setSub] = useState(null);
  const [planMeta, setPlanMeta] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [a, b] = await Promise.all([
          api.get('/api/subscriptions/me'),
          api.get('/api/subscriptions/plans'),
        ]);
        if (!alive) return;
        setSub(a.data?.subscription || null);
        const plans = b.data?.plans || [];
        setAllPlans(plans.filter((p) => p.is_active !== false));
        setPlanMeta(plans.find((p) => p.name === user?.subscription_plan) || plans.find((p) => tierKey(p.name) === tierKey(user?.subscription_plan)) || null);
      } catch (e) {/* silent */}
      finally { if (alive) setLoading(false); }
    };
    load();
    return () => { alive = false; };
  }, [api, user?.subscription_plan]);

  const isActive = user?.subscription_status === 'active';

  // Already on top tier? Then upgrade is disabled.
  const topPrice = Math.max(0, ...allPlans.map((p) => Number(p.price || 0)));
  const onTopTier = isActive && planMeta && Number(planMeta.price || 0) >= topPrice;

  if (loading) {
    return (
      <MemberSidebarLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </MemberSidebarLayout>
    );
  }

  return (
    <MemberSidebarLayout>
      <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="my-plan-page">
        <div className="mb-6">
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white flex items-center gap-2">
            <Crown className="w-7 h-7 text-[#FFEA00]" /> My Plan
          </h1>
          <p className="text-[#A0B4CC] mt-1 text-sm">
            Your active AGA membership at a glance.
          </p>
        </div>

        {isActive ? (
          <Card className="bg-[#041A3B] border-[#00E676]/30" data-testid="my-plan-active-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#00E676]" /> Active
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <StatTile label="Current Plan" value={user?.subscription_status === 'active' && user?.subscription_plan ? tierLabel(user.subscription_plan) : 'Free'} accent="#FFEA00" testid="my-plan-name" />
                <StatTile label="Plan Price" value={fmtPHP(planMeta?.price)} accent="#00E5FF" testid="my-plan-price" />
                <StatTile label="Activated" value={fmtDate(sub?.start_date || sub?.created_at)} accent="#A0B4CC" testid="my-plan-activated" />
                <StatTile label="Next Billing" value={fmtDate(sub?.next_billing_date || sub?.end_date)} accent="#A0B4CC" testid="my-plan-next-billing" />
              </div>

              {planMeta?.description && (
                <p className="text-[#A0B4CC] text-sm leading-relaxed border-t border-white/5 pt-3">
                  {planMeta.description}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button
                  onClick={() => setUpgradeOpen(true)}
                  disabled={onTopTier}
                  className="bg-[#00E676] text-[#001627] hover:bg-[#00C853] disabled:opacity-40 flex-1"
                  data-testid="my-plan-upgrade-btn"
                  title={onTopTier ? "You're already on the highest tier" : 'Upgrade your plan'}
                >
                  <TrendingUp className="w-4 h-4 mr-1.5" />
                  {onTopTier ? 'You\'re on the top tier' : 'Upgrade Plan'}
                </Button>
                <Link to="/wallet" className="flex-1">
                  <Button variant="outline" className="border-white/20 text-[#A0B4CC] hover:text-white w-full" data-testid="my-plan-wallet-link">
                    <WalletIcon className="w-4 h-4 mr-1.5" />
                    Wallet & Billing
                  </Button>
                </Link>
              </div>

              <div className="text-[10px] text-[#5C7599] flex items-start gap-1 pt-2 border-t border-white/5">
                <Calendar className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>
                  Upgrades are pro-rated against your remaining balance. You only pay the difference — no double charges.
                </span>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[#041A3B] border-white/10" data-testid="my-plan-inactive-card">
            <CardContent className="p-6 text-center space-y-3">
              <Crown className="w-12 h-12 text-[#A0B4CC] mx-auto" />
              <p className="text-[#A0B4CC] text-xs uppercase tracking-wider">Current plan</p>
              <p className="text-white font-outfit text-2xl font-semibold" data-testid="my-plan-name">Free</p>
              <p className="text-[#A0B4CC] text-sm">
                You can browse every partner merchant and offer. Upgrade to Pro or Elite to get the member discounts,
                plus your wallet, certificate and referral commissions.
              </p>
              <Link to="/pricing">
                <Button className="btn-primary" data-testid="my-plan-pick-plan-btn">
                  Upgrade to Pro or Elite
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
      <UpgradePlanModal open={upgradeOpen} onOpenChange={setUpgradeOpen} onUpgraded={() => window.location.reload()} />
    </MemberSidebarLayout>
  );
}

function StatTile({ label, value, accent, testid }) {
  return (
    <div className="rounded-lg border border-white/5 bg-[#000B18]/60 p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-[#5C7599]">{label}</p>
      <p className="font-outfit text-base font-semibold mt-1" style={{ color: accent }}>{value}</p>
    </div>
  );
}

export default MyPlanPage;
